import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { Device, from, until } from '@helpers/media';
import scrollbars from '@helpers/scrollbars';
import { h3, h4 } from '@helpers/typography';
import CartProductCardStyles from '@stories/Components/Cards/CartProductCard/CartProductCard.styles';
import ButtonStyles from '@stories/Components/Global/Buttons/Button/Button.styles';
import styled from 'styled-components';
const Container = styled.section `
  --tintColor: rgba(0, 0, 0, 0.75);

  display: contents;

  &[data-site='ess'] {
    --bgColour: ${brand.global.white};
    --upsellBgColour: ${brand.ess.secondary.lightestGrey};
    --closeColour: ${brand.ess.primary};
    --borderColour: ${brand.ess.secondary.lightGrey};
    --titleColour: ${brand.global.red};
  }

  &[data-site='mep'] {
    --bgColour: ${brand.global.white};
    --upsellBgColour: ${brand.mep.secondary.lightGrey};
    --closeColour: ${brand.mep.primary};
    --borderColour: ${brand.mep.secondary.midGrey};
    --titleColour: ${brand.global.red};
  }
`;
const Drawer = styled.div `
  ${transition('transform', 1)};

  --paddingTop: ${fluid(20, 56)};

  position: fixed;
  bottom: 0;
  right: 0;
  display: block;
  width: 100%;
  height: calc(100% - var(--headerHeight, 0px));
  max-width: 640px;
  transform: translateX(100%);
  display: flex;
  z-index: 9999;

  [data-open='true'] & {
    transform: translateX(0);
  }

  @media ${from(Device.Tablet)} {
    height: 100%;
  }

  @media ${from(Device.DesktopSmall)} {
    &[data-has-upsell='true'] {
      max-width: 1550px;
    }
  }
`;
const DrawerMain = styled.div `
  height: 100%;
  width: 100%;
  background: var(--bgColour);
  display: flex;
  flex-direction: column;
  padding: var(--paddingTop) var(--sitePadding) ${fluid(42, 96)};
  flex: 1 1 100%;
  border-left: 1px solid var(--borderColour);
  z-index: 1;

  @media ${from(Device.Tablet)} {
    padding-left: 24px;
  }
`;
const InnerWrapper = styled.div `
  display: grid;
  position: relative;
  overflow: hidden;

  @media ${from(Device.DesktopSmall)} {
    display: contents;
  }
`;
const UpsellContainer = styled.div `
  position: relative;
  height: 100%;
  overflow: auto;
  background-color: var(--upsellBgColour);
  z-index: 1;
  padding: 20px;

  @media ${until(Device.DesktopSmall)} {
    grid-area: 1 / 1 / 2 / 2;
  }

  @media ${from(Device.DesktopSmall)} {
    ${transition('transform', 2)};
    ${scrollbars};

    flex: 0 0 66%;
    transform: translateX(150%);
    transition-delay: 0.66s;
    padding: var(--paddingTop) ${fluid(32, 42)} var(--paddingTop) ${fluid(36, 54)};
    margin: 0;

    [data-open='true'] & {
      transform: translateX(0);
    }
  }
`;
const UpsellTitle = styled.button `
  ${ButtonReset};
  ${h4};

  display: flex;
  gap: 12px;
  justify-content: space-between;
  align-items: center;

  [data-site='ess'] & {
    color: ${brand.ess.secondary.darkestGrey};
  }

  [data-site='mep'] & {
    ${fonts.museoSlab.medium};

    color: ${brand.mep.primary};

    > span {
      ${fonts.daxWide.bold};
    }
  }

  svg {
    display: block;
    width: 12px;
    transform: rotate(90deg);
  }

  @media ${from(Device.DesktopSmall)} {
    cursor: default;

    svg {
      display: none;
    }
  }
`;
const TitleWrapper = styled.div `
  display: flex;
  gap: 24px;
  justify-content: space-between;
  border-bottom: 1px solid var(--borderColour);
  margin: 0 0 ${fluid(20, 24)};
  padding: 0 0 ${fluid(20, 24)};
`;
const Title = styled.p `
  ${h3};

  color: var(--titleColour);
  margin: 0;

  ${Container}[data-site='mep'] & {
    ${fonts.museoSlab.bold};
  }
`;
const UpsellGrid = styled.div `
  display: grid;
  gap: ${fluid(8, 24)};

  @media ${from(Device.DesktopSmall)} {
    grid-template-columns: repeat(2, 1fr);
  }
`;
const CloseButton = styled.button `
  ${ButtonReset};
  ${transition('opacity, transform')};

  color: var(--closeColour);
  width: ${fluid(16, 22, Device.TabletLarge)};
  height: ${fluid(16, 22, Device.TabletLarge)};

  [data-site='ess'] & {
    top: ${fluid(16, 22, Device.TabletLarge)};
  }

  > svg {
    display: block;
  }

  &.focus-ring {
    outline: auto;
    outline-offset: 5px;
    outline-width: 2px;
    outline-style: solid;
  }

  &.focus-ring,
  &:hover {
    opacity: 0.7;
    transform: scale(1.1);
  }
`;
const CardWrapper = styled.div `
  ${scrollbars};

  overflow: auto;
  margin: 0 -8px ${fluid(20, 24)} 0;
  padding: 0 8px 0 0;

  @media ${until(Device.DesktopSmall)} {
    grid-area: 1 / 1 / 2 / 2;
  }

  ${CartProductCardStyles.Wrapper}:not(:last-of-type) {
    margin: 0 0 ${fluid(12, 20)};
  }
`;
const ButtonWrapper = styled.div `
  border-top: 1px solid var(--borderColour);
  display: flex;
  flex-wrap: wrap;
  gap: ${fluid(20, 24)};
  margin: auto 0 0;
  padding: ${fluid(20, 24)} 0 0;

  ${Container}[data-site='mep'] & {
    justify-content: space-between;

    @media ${from(Device.Tablet)} {
      justify-content: center;
    }

    ${ButtonStyles.Container}[data-type='outline'] {
      --colourText: ${brand.global.red};
    }
  }

  @media ${until(Device.TabletLarge)} {
    ${Container}[data-site='ess'] & {
      column-gap: calc(${fluid(20, 24)} + 32px);
    }

    ${ButtonStyles.Container}[data-type='secondary'] {
      --colourBorder: ${brand.global.red};
      --colourBg: ${brand.global.red};
    }
  }
`;
const BackgroundChevron = styled.div `
  display: none;
  color: ${brand.ess.secondary.lightGrey};

  @media ${from(Device.TabletLarge)} {
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    z-index: 0;

    > svg {
      display: block;
      height: 850px;
      transform: translateX(-45%);
    }
  }
`;
const NoProductsMessage = styled.p `
  @media ${until(Device.DesktopSmall)} {
    grid-area: 1 / 1 / 2 / 2;
  }
`;
const CartOverlayStyles = {
    Container,
    Drawer,
    TitleWrapper,
    Title,
    CardWrapper,
    ButtonWrapper,
    CloseButton,
    UpsellContainer,
    DrawerMain,
    BackgroundChevron,
    UpsellTitle,
    UpsellGrid,
    InnerWrapper,
    NoProductsMessage,
};
export default CartOverlayStyles;
